import { useEffect, useState } from "react";
import { api, ApiError, type ContentField } from "../lib/api.ts";
import { Accented } from "../components/Accented.tsx";

interface ContentEditorProps {
  token: string;
  onUnauthorised: () => void;
}

const SECTION_LABELS: Record<string, { title: string; hint: string }> = {
  hero: {
    title: "Top of the page",
    hint: "The first thing anyone sees. Worth getting right.",
  },
  treatments: {
    title: "Treatments section",
    hint:
      "An introduction heading and paragraph side by side, then the small label above the " +
      "signature treatment. Which treatment is the signature is chosen under Treatments.",
  },
  about: {
    title: "About section",
    hint: "Introducing you. Optional lines left blank are simply left off the page.",
  },
  ethos: {
    title: "The Studio section",
    hint: "Who you are and how you work.",
  },
  visit: {
    title: "Visit section",
    hint:
      "Directions to the studio, shown with the opening hours and contact details. The hours " +
      "themselves are changed under Opening hours.",
  },
  email: {
    title: "Emails",
    hint:
      "The wording of the emails clients receive - the confirmation, the reminder the day " +
      "before, the nudge about the consultation form, and the cancellation. The appointment " +
      "details, links and prices are added around whatever you write here.",
  },
};

export function ContentEditor({ token, onUnauthorised }: ContentEditorProps) {
  const [fields, setFields] = useState<ContentField[] | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api.admin
      .getContent(token)
      .then((result) => {
        setFields(result.fields);
        setDraft(
          Object.fromEntries(result.fields.map((f) => [f.key, f.value])),
        );
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.status === 401)
          return onUnauthorised();
        setError("Site wording could not be loaded.");
      });
  }, [token, onUnauthorised]);

  const dirtyKeys = (fields ?? [])
    .filter((field) => (draft[field.key] ?? "") !== field.value)
    .map((field) => field.key);

  // Only a required field holds up the save. A blank optional one is a
  // deliberate choice: the page leaves that line out.
  const missingKeys = (fields ?? [])
    .filter(
      (field) =>
        !field.optional && (draft[field.key] ?? "").trim().length === 0,
    )
    .map((field) => field.key);

  async function save() {
    if (!fields) return;
    setBusy(true);
    setError(null);
    try {
      const values = Object.fromEntries(
        dirtyKeys.map((key) => [key, draft[key] ?? ""]),
      );
      const result = await api.admin.saveContent(token, values);
      setFields(result.fields);
      setDraft(Object.fromEntries(result.fields.map((f) => [f.key, f.value])));
      setMessage(
        result.applied.length === 1
          ? "Saved. The website is updated."
          : `${result.applied.length} changes saved. The website is updated.`,
      );
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "That could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!fields) {
    return (
      <section className="page">
        {error ? (
          <p className="notice notice--error">{error}</p>
        ) : (
          <p className="booking__loading">
            <span className="spinner" /> Loading wording…
          </p>
        )}
      </section>
    );
  }

  // The API returns sections ordered by name, which is alphabetical and
  // meaningless to a reader. Present them in the order they appear down the
  // page, with anything unrecognised last rather than dropped.
  const pageOrder = Object.keys(SECTION_LABELS);
  const sections = [...new Set(fields.map((field) => field.section))].sort(
    (a, b) => {
      const ai = pageOrder.indexOf(a);
      const bi = pageOrder.indexOf(b);
      return (
        (ai === -1 ? Number.MAX_SAFE_INTEGER : ai) -
        (bi === -1 ? Number.MAX_SAFE_INTEGER : bi)
      );
    },
  );

  return (
    <section className="page stack-lg">
      <div>
        <h2 className="admin__section-title">Site wording</h2>
        <p className="field-hint">
          Every heading and paragraph on the public page. Changes appear on the
          website immediately. Prices, opening hours and the cancellation policy
          are not here - those live under Treatments and Booking rules, where
          they can be checked properly.
        </p>
      </div>

      <p className="notice">
        Wrap a word or phrase in <strong>*asterisks*</strong> to give it the
        italic accent the design uses - as in{" "}
        <q>Skin, stillness and *renewal*</q>.
      </p>

      {sections.map((section) => {
        const meta = SECTION_LABELS[section] ?? { title: section, hint: "" };
        const sectionFields = fields.filter(
          (field) => field.section === section,
        );

        return (
          <section key={section} className="content-section">
            <div className="content-section__head">
              <h3 className="content-section__title">{meta.title}</h3>
              {meta.hint && <p className="field-hint">{meta.hint}</p>}
            </div>

            {sectionFields.map((field) => {
              const value = draft[field.key] ?? "";
              const blank = value.trim().length === 0;
              const missing = blank && !field.optional;
              const changed = value !== field.value;

              return (
                <div key={field.key} className="field content-field">
                  <label htmlFor={`content-${field.key}`}>
                    {field.optional ? `${field.label} (optional)` : field.label}
                    {changed && (
                      <span className="content-field__changed">changed</span>
                    )}
                  </label>

                  {field.isLong ? (
                    <textarea
                      id={`content-${field.key}`}
                      className="textarea"
                      rows={4}
                      maxLength={1200}
                      value={value}
                      aria-invalid={missing}
                      onChange={(event) => {
                        setDraft((d) => ({
                          ...d,
                          [field.key]: event.target.value,
                        }));
                        setMessage(null);
                      }}
                    />
                  ) : (
                    <input
                      id={`content-${field.key}`}
                      className="input"
                      maxLength={1200}
                      value={value}
                      aria-invalid={missing}
                      onChange={(event) => {
                        setDraft((d) => ({
                          ...d,
                          [field.key]: event.target.value,
                        }));
                        setMessage(null);
                      }}
                    />
                  )}

                  {field.hint && <p className="field-hint">{field.hint}</p>}
                  {missing && (
                    <p className="field-error">This cannot be left blank.</p>
                  )}

                  {/* Only worth previewing where the asterisk accent applies. */}
                  {value.includes("*") && !blank && (
                    <p className="content-field__preview">
                      <span className="label">Preview</span>
                      <span className="content-field__preview-text">
                        <Accented text={value} />
                      </span>
                    </p>
                  )}
                </div>
              );
            })}
          </section>
        );
      })}

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      <div className="row admin__sticky-actions">
        <button
          type="button"
          className="btn"
          onClick={save}
          disabled={busy || dirtyKeys.length === 0 || missingKeys.length > 0}
        >
          {busy ? (
            <span className="spinner" />
          ) : dirtyKeys.length === 0 ? (
            "No changes"
          ) : (
            `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}`
          )}
        </button>
        {dirtyKeys.length > 0 && (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              setDraft(Object.fromEntries(fields.map((f) => [f.key, f.value])));
              setMessage(null);
            }}
          >
            Discard changes
          </button>
        )}
      </div>
    </section>
  );
}
